import { message } from 'antd';
import { barcodeScanApi, type BarcodeScan } from '../api/barcodeScans';
import useBarcodeScanStore from '../store/barcodeScanStore';
import useScanTargetStore from '../store/scanTargetStore';

export type BarcodeProcessResult =
  | 'added' | 'rejected' | 'awaiting_binding' | 'no_target' | 'error';

const processing = new Set<number>();

export const processBarcodeScan = async (
  scan: BarcodeScan,
  manual = false,
): Promise<BarcodeProcessResult> => {
  if (processing.has(scan.id)) return 'error';
  const handler = useScanTargetStore.getState().barcodeHandler;
  if (!handler) {
    if (manual) message.info('Откройте заказ-наряд, чтобы добавить товар');
    return 'no_target';
  }

  processing.add(scan.id);
  try {
    let product;
    try {
      product = await barcodeScanApi.getProduct(scan.raw_code);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        const detail = e?.response?.data?.detail;
        const knowledge = detail && typeof detail === 'object' ? detail.knowledge ?? null : null;
        const enrichedScan = { ...scan, knowledge };
        useBarcodeScanStore.setState(state => ({
          items: state.items.map(item => item.id === scan.id ? { ...item, knowledge } : item),
        }));
        await barcodeScanApi.ack(scan.id, 'awaiting_binding');
        useBarcodeScanStore.getState().updateOutcome(scan.id, 'awaiting_binding');
        useBarcodeScanStore.getState().openBinding({ ...enrichedScan, outcome: 'awaiting_binding' });
        return 'awaiting_binding';
      }
      throw e;
    }

    useBarcodeScanStore.setState(state => ({
      items: state.items.map(item => item.id === scan.id ? { ...item, product } : item),
    }));
    const result = await handler(product);
    if (result.ok) {
      await barcodeScanApi.ack(scan.id, 'added');
      useBarcodeScanStore.getState().removeItem(scan.id);
      message.success(result.message || `«${product.name}» добавлен`);
      return 'added';
    }

    await barcodeScanApi.ack(scan.id, 'rejected');
    useBarcodeScanStore.getState().updateOutcome(scan.id, 'rejected');
    if (result.duplicate) message.warning(result.message || 'Товар уже добавлен');
    else message.error(result.message || 'Не удалось добавить товар');
    return 'rejected';
  } catch (e: any) {
    message.error(e?.response?.data?.detail || 'Ошибка поиска товара по штрихкоду');
    return 'error';
  } finally {
    processing.delete(scan.id);
  }
};
